"use client";

import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PricingPlans() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const {
    ref: card1Ref,
    isVisible: card1Visible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: card2Ref,
    isVisible: card2Visible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: card3Ref,
    isVisible: card3Visible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const plans = [{
    name: "Starter",
    price: "£29",
    period: "/month",
    description: "Perfect for small businesses getting started with cloud accounting",
    features: ["Up to 10 invoices per month", "Basic receipt scanning", "Email support", "1 user account", "Monthly reports"],
    popular: false
  }, {
    name: "Professional",
    price: "£79",
    period: "/month",
    description: "Ideal for growing businesses with advanced needs",
    features: ["Unlimited invoices", "Advanced receipt scanning", "Priority support", "Up to 5 user accounts", "Real-time reporting", "Expense tracking", "Multi-currency support"],
    popular: true
  }, {
    name: "Enterprise",
    price: "£149",
    period: "/month",
    description: "Complete solution for established businesses",
    features: ["Everything in Professional", "Dedicated account manager", "Custom integrations", "Unlimited user accounts", "Advanced forecasting", "API access", "White-label options"],
    popular: false
  }];
  return <section id="pricing-plans" className="bg-[hsl(0,0%,10%)] text-[hsl(0,0%,90%)] py-24 md:py-32">
      <div className="container mx-auto px-4 max-w-7xl">
        <div ref={headerRef} className={`text-center mb-16 transition-all duration-500 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-[48px] leading-[1.2] font-bold tracking-[-0.2px] mb-4">
            Pricing Plans
          </h2>
          <p className="text-[16px] leading-[1.6] text-[hsl(0,0%,75%)] max-w-2xl mx-auto">
            Choose the perfect plan for your business needs. All plans include cloud-based access and automatic updates.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 items-start">
          <div ref={card1Ref} data-index={0} className={`transition-all duration-500 delay-100 ${card1Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Card className="bg-[hsl(0,0%,15%)] border-[hsl(0,0%,30%)] text-[hsl(0,0%,90%)] h-full flex flex-col hover:border-primary transition-all duration-300 hover:shadow-[0_0_20px_rgba(59,130,246,0.3)]">
              <CardHeader>
                <CardTitle className="text-[28px] leading-[1.4] font-semibold tracking-[-0.2px]">
                  {plans[0].name}
                </CardTitle>
                <CardDescription className="text-[hsl(0,0%,75%)]">
                  {plans[0].description}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="mb-6">
                  <span className="text-[48px] leading-[1.2] font-bold tracking-[-0.2px]">{plans[0].price}</span>
                  <span className="text-[16px] leading-[1.6] text-[hsl(0,0%,75%)]">{plans[0].period}</span>
                </div>
                <ul className="space-y-3">
                  {plans[0].features.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                      <Check className="size-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-[16px] leading-[1.6]">{feature}</span>
                    </li>)}
                </ul>
              </CardContent>
              <CardFooter>
                <Button className="w-full bg-primary text-foreground-primary hover:bg-primary-active rounded-[var(--radius-full)] font-semibold transition-all duration-200 hover:scale-105">
                  Get Started
                </Button>
              </CardFooter>
            </Card>
          </div>

          <div ref={card2Ref} data-index={1} className={`transition-all duration-500 delay-200 ${card2Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Card className="bg-[hsl(0,0%,15%)] border-primary text-[hsl(0,0%,90%)] h-full flex flex-col shadow-[0_0_24px_rgba(59,130,246,0.25)] hover:shadow-[0_0_32px_rgba(59,130,246,0.35)] transition-all duration-300 relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-foreground-primary px-4 py-1 rounded-[var(--radius-full)] text-sm font-semibold">
                Most Popular
              </div>
              <CardHeader>
                <CardTitle className="text-[28px] leading-[1.4] font-semibold tracking-[-0.2px]">
                  {plans[1].name}
                </CardTitle>
                <CardDescription className="text-[hsl(0,0%,75%)]">
                  {plans[1].description}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="mb-6">
                  <span className="text-[48px] leading-[1.2] font-bold tracking-[-0.2px]">{plans[1].price}</span>
                  <span className="text-[16px] leading-[1.6] text-[hsl(0,0%,75%)]">{plans[1].period}</span>
                </div>
                <ul className="space-y-3">
                  {plans[1].features.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                      <Check className="size-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-[16px] leading-[1.6]">{feature}</span>
                    </li>)}
                </ul>
              </CardContent>
              <CardFooter>
                <Button className="w-full bg-primary text-foreground-primary hover:bg-primary-active rounded-[var(--radius-full)] font-semibold transition-all duration-200 hover:scale-105">
                  Get Started
                </Button>
              </CardFooter>
            </Card>
          </div>

          <div ref={card3Ref} data-index={2} className={`transition-all duration-500 delay-300 ${card3Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Card className="bg-[hsl(0,0%,15%)] border-[hsl(0,0%,30%)] text-[hsl(0,0%,90%)] h-full flex flex-col hover:border-primary transition-all duration-300 hover:shadow-[0_0_20px_rgba(59,130,246,0.3)]">
              <CardHeader>
                <CardTitle className="text-[28px] leading-[1.4] font-semibold tracking-[-0.2px]">
                  {plans[2].name}
                </CardTitle>
                <CardDescription className="text-[hsl(0,0%,75%)]">
                  {plans[2].description}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="mb-6">
                  <span className="text-[48px] leading-[1.2] font-bold tracking-[-0.2px]">{plans[2].price}</span>
                  <span className="text-[16px] leading-[1.6] text-[hsl(0,0%,75%)]">{plans[2].period}</span>
                </div>
                <ul className="space-y-3">
                  {plans[2].features.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                      <Check className="size-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-[16px] leading-[1.6]">{feature}</span>
                    </li>)}
                </ul>
              </CardContent>
              <CardFooter>
                <Button className="w-full bg-primary text-foreground-primary hover:bg-primary-active rounded-[var(--radius-full)] font-semibold transition-all duration-200 hover:scale-105">
                  Get Started
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}